'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Link01Icon, Message01Icon } from '@/components/ui/icon/glyphs';
import { TOAST_IDS } from '@/config/toast-ids';
import { Link } from '@/i18n/navigation';
import { canNativeShare, copyToClipboard, nativeShare, openShareIntent } from '@/lib/share/share';
import { ShareCopyField } from '@/lib/share/share-copy-field';
import { ShareOgPreview } from '@/lib/share/share-og-preview';
import { SharePlatformRow } from '@/lib/share/share-platform-row';
import { PLATFORM_CHANNELS, type PlatformChannel } from '@/lib/share/share-platforms';
import { showToast } from '@/lib/ui/toast';
const subscribeNoop: (onStoreChange: () => void) => () => void = () => (): void => {};
const getNativeShareSnapshot: () => boolean = () => canNativeShare();
const getNativeShareServerSnapshot: () => boolean = () => false;
interface ProposalCreatedSuccessProps {
  /** 64-char hex SHA-256 of the proposal title — proposal route slug. */
  proposalPubkey: string;
  /** Original proposal title — surfaced as the modal header + share text. */
  title: string;
  /** Reuses the owning dialog's successful close/reset path before navigation. */
  onViewMarket: () => void;
}

/** Success / share view rendered in-place inside the Create Proposal
 *  dialog after a confirmed on-chain submission. The view IS the share
 *  surface — channel buttons + copy field are inline; no nested modal.
 *  Tone is clinical and editorial: restrained copy with asymmetric
 *  typography over centred decoration.
 *
 *  Reveal animation is delegated to Tailwind's `animate-in` utilities so
 *  there is zero Motion runtime cost on a view that mounts at most once
 *  per proposal — and zero risk of children getting stuck at opacity-0
 *  if a feature loader fails. */
export function ProposalCreatedSuccess(t0) {
  const $ = _c(82);
  const {
    proposalPubkey,
    title,
    onViewMarket
  } = t0;
  const t = useTranslations("launchpad.form.success");
  const tShare = useTranslations("share.modal");
  const proposalPath = `/proposals/${proposalPubkey}`;
  const proposalUrl = typeof window !== "undefined" ? `${window.location.origin}${proposalPath}` : "";
  let t1;
  if ($[0] !== t || $[1] !== title) {
    t1 = t("shareTweet", {
      title
    });
    $[0] = t;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const shareText = t1;
  const fullMessage = `${shareText} ${proposalUrl}`;
  const hasNative = useSyncExternalStore(subscribeNoop, getNativeShareSnapshot, getNativeShareServerSnapshot);
  const [copied, setCopied] = useState(false);
  let t2;
  let t3;
  if ($[3] !== copied) {
    t2 = () => {
      if (!copied) {
        return;
      }
      const timer = setTimeout(() => {
        setCopied(false);
      }, 2000);
      return () => {
        clearTimeout(timer);
      };
    };
    t3 = [copied];
    $[3] = copied;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== proposalPubkey) {
    t4 = {
      kind: "proposal" as const,
      proposalPubkey
    };
    $[6] = proposalPubkey;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const surface = t4;
  let t5;
  if ($[8] !== proposalUrl || $[9] !== shareText || $[10] !== surface) {
    t5 = function handleChannel(channel) {
      openShareIntent(channel, {
        url: proposalUrl,
        text: shareText,
        surface
      });
    };
    $[8] = proposalUrl;
    $[9] = shareText;
    $[10] = surface;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleChannel = t5;
  let t6;
  if ($[12] !== proposalUrl || $[13] !== shareText || $[14] !== surface || $[15] !== t) {
    t6 = async function handleNative() {
      await nativeShare({
        url: proposalUrl,
        text: shareText,
        title: t("title"),
        surface
      });
    };
    $[12] = proposalUrl;
    $[13] = shareText;
    $[14] = surface;
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const handleNative = t6;
  let t7;
  if ($[17] !== proposalUrl || $[18] !== tShare) {
    t7 = async function handleCopyLink() {
      const ok = await copyToClipboard(proposalUrl);
      if (!ok) {
        return;
      }
      setCopied(true);
      showToast({
        id: TOAST_IDS.SHARE_LINK_COPIED,
        message: tShare("linkCopied"),
        variant: "success"
      });
    };
    $[17] = proposalUrl;
    $[18] = tShare;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const handleCopyLink = t7;
  let t8;
  if ($[20] !== fullMessage || $[21] !== tShare) {
    t8 = async function handleCopyMessage() {
      const ok_0 = await copyToClipboard(fullMessage);
      if (!ok_0) {
        return;
      }
      setCopied(true);
      showToast({
        id: TOAST_IDS.SHARE_LINK_COPIED,
        message: tShare("messageCopied"),
        variant: "success"
      });
    };
    $[20] = fullMessage;
    $[21] = tShare;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const handleCopyMessage = t8;
  let t9;
  if ($[23] !== surface || $[24] !== title) {
    t9 = <ShareOgPreview surface={surface} alt={title} />;
    $[23] = surface;
    $[24] = title;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t) {
    t10 = t("body");
    $[26] = t;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10) {
    t11 = <p className="text-body text-muted-foreground">{t10}</p>;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== tShare) {
    t12 = tShare("viaLabel");
    $[30] = tShare;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t12) {
    t13 = <p className="text-caption font-medium text-muted-foreground">{t12}</p>;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== handleNative) {
    t14 = () => {
      handleNative();
    };
    $[34] = handleNative;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== handleChannel || $[37] !== hasNative || $[38] !== t14) {
    t15 = <SharePlatformRow channels={PLATFORM_CHANNELS} showNative={hasNative} onChannel={handleChannel} onNative={t14} />;
    $[36] = handleChannel;
    $[37] = hasNative;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t13 || $[41] !== t15) {
    t16 = <div className="flex flex-col gap-3">{t13}{t15}</div>;
    $[40] = t13;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== tShare) {
    t17 = tShare("orCopyLink");
    $[43] = tShare;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== handleCopyLink) {
    t18 = () => {
      handleCopyLink();
    };
    $[45] = handleCopyLink;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== tShare) {
    t19 = tShare("copy");
    $[47] = tShare;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== copied || $[50] !== proposalUrl || $[51] !== t17 || $[52] !== t18 || $[53] !== t19 || $[54] !== tShare) {
    t20 = <ShareCopyField label={t17} prefixIcon={Link01Icon} value={proposalUrl} wrapMode="break-all" onCopy={t18} t={tShare} copied={copied} copyAriaLabel={t19} />;
    $[49] = copied;
    $[50] = proposalUrl;
    $[51] = t17;
    $[52] = t18;
    $[53] = t19;
    $[54] = tShare;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== tShare) {
    t21 = tShare("orCopyMessage");
    $[56] = tShare;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== handleCopyMessage) {
    t22 = () => {
      handleCopyMessage();
    };
    $[58] = handleCopyMessage;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== tShare) {
    t23 = tShare("copyMessage");
    $[60] = tShare;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== copied || $[63] !== fullMessage || $[64] !== t21 || $[65] !== t22 || $[66] !== t23 || $[67] !== tShare) {
    t24 = <ShareCopyField label={t21} prefixIcon={Message01Icon} value={fullMessage} wrapMode="wrap-anywhere" onCopy={t22} t={tShare} copied={copied} copyAriaLabel={t23} />;
    $[62] = copied;
    $[63] = fullMessage;
    $[64] = t21;
    $[65] = t22;
    $[66] = t23;
    $[67] = tShare;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[69] !== t) {
    t25 = t("viewMarket");
    $[69] = t;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  let t26;
  if ($[71] !== onViewMarket || $[72] !== proposalPath || $[73] !== t25) {
    t26 = <AnimatedButton asChild={true} variant="primary" size="xl" className="w-full"><Link href={proposalPath} onClick={onViewMarket}>{t25}</Link></AnimatedButton>;
    $[71] = onViewMarket;
    $[72] = proposalPath;
    $[73] = t25;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  let t27;
  if ($[75] !== t11 || $[76] !== t16 || $[77] !== t20 || $[78] !== t24 || $[79] !== t26 || $[80] !== t9) {
    t27 = <div className="flex flex-col gap-5 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 motion-safe:duration-normal">{t9}{t11}{t16}{t20}{t24}{t26}</div>;
    $[75] = t11;
    $[76] = t16;
    $[77] = t20;
    $[78] = t24;
    $[79] = t26;
    $[80] = t9;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  return t27;
}